import { useState, useCallback, useEffect } from "react";
import { Chip, CircularProgress, Collapse, Divider, Tooltip } from "@mui/material";
import { useHistory } from "react-router-dom";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useAuthz } from "context/AuthzContext";
import { auth } from "../../firebase";
import { TC, FS, FW } from "../../tokens/text";

const API         = process.env.REACT_APP_ACCOUNTING_API_BASE_URL || "http://localhost:4001/api/v1";
const STORAGE_KEY = "ledger_healthcheck_history";
const RETAIN_MS   = 7 * 24 * 60 * 60 * 1000;

function loadHistory() {
  try {
    const raw    = localStorage.getItem(STORAGE_KEY);
    const all    = raw ? JSON.parse(raw) : [];
    const cutoff = Date.now() - RETAIN_MS;
    return all.filter((r) => new Date(r.ranAt).getTime() > cutoff);
  } catch { return []; }
}
function saveHistory(runs) {
  try {
    const pruned = runs.filter((r) => new Date(r.ranAt).getTime() > Date.now() - RETAIN_MS);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(pruned));
    return pruned;
  } catch { return runs; }
}

async function getToken() {
  const user = auth.currentUser;
  if (!user) return null;
  try { return await user.getIdToken(false); } catch { return await user.getIdToken(true); }
}
async function ping(path, { auth: needsAuth = true } = {}) {
  const start = Date.now();
  try {
    const headers = { "Content-Type": "application/json" };
    if (needsAuth) { const t = await getToken(); if (t) headers["Authorization"] = `Bearer ${t}`; }
    const res = await fetch(`${API}${path}`, { headers });
    return { status: res.status, ok: res.status < 400, ms: Date.now() - start };
  } catch (err) {
    return { status: null, ok: false, ms: Date.now() - start, error: err.message };
  }
}

const GROUPS = [
  { group: "Core", endpoints: [
    { label: "Health",
      path: "/health", auth: false,
      usage: "Backend liveness check",
      pages: [{ label: "Health Check", route: "/health-check" }] },
  ]},
  { group: "Auth & Users", endpoints: [
    { label: "Auth — me",
      path: "/auth/me",
      usage: "Loads the current user's profile and role",
      pages: [] },
    { label: "Tenant users",
      path: "/tenant/users", query: (t) => `?tenantId=${t}`,
      usage: "Lists all staff/users in the tenant",
      pages: [{ label: "Tenant Users", route: "/tenant-users" }] },
    { label: "Tenant roles",
      path: "/tenant/roles", query: (t) => `?tenantId=${t}`,
      usage: "Fetches available roles for the tenant",
      pages: [{ label: "Role Assignment", route: "/role-assignment" }] },
    { label: "Organisations",
      path: "/orgs",
      usage: "Lists organisations the current user belongs to",
      pages: [{ label: "Organisations", route: "/organisations" }] },
  ]},
  { group: "Clients", endpoints: [
    { label: "Client profiles",
      path: (t) => `/tenant/${t}/client-profiles`,
      usage: "Fetches all client records · used by Clients list, CS01, AD01 wizards",
      pages: [{ label: "Clients", route: "/client-profiles" }, { label: "Jobs Board", route: "/jobs-board" }] },
    { label: "Directors",
      path: (t) => `/tenant/${t}/directors`,
      usage: "Fetches director records linked to clients · used by AD01 form",
      pages: [{ label: "Directors", route: "/directors" }] },
  ]},
  { group: "Jobs & Tasks", endpoints: [
    { label: "Tasks",
      path: (t) => `/tenant/${t}/tasks`,
      usage: "Fetches all tasks across jobs",
      pages: [{ label: "Jobs Board", route: "/jobs-board" }, { label: "Tasks", route: "/tasks" }] },
    { label: "Jobs",
      path: (t) => `/tenant/${t}/jobs`,
      usage: "Fetches all jobs for the tenant",
      pages: [{ label: "Jobs Board", route: "/jobs-board" }] },
    { label: "Services (master)",
      path: "/services/master",
      usage: "Loads the master service catalogue",
      pages: [{ label: "Settings", route: "/settings" }] },
  ]},
  { group: "Email", endpoints: [
    { label: "Email config",
      path: (t) => `/tenant/${t}/email-config`,
      usage: "Loads SMTP / Mailchimp settings · reply-to · from name",
      pages: [{ label: "Email Config", route: "/email-config" }] },
    { label: "Email templates",
      path: (t) => `/tenant/${t}/email-templates`,
      usage: "Saved email templates · used by CS01 review email · AD01 notification",
      pages: [{ label: "Email Config", route: "/email-config" }] },
  ]},
  { group: "Confirmation Statements", endpoints: [
    { label: "CS — list",
      path: (t) => `/tenant/${t}/confirmation-statements`,
      usage: "Lists all CS01 confirmation statements · used by CS01 filing wizard",
      pages: [{ label: "Clients", route: "/client-profiles" }] },
  ]},
  { group: "VAT", endpoints: [
    { label: "VAT settings",
      path: (t) => `/tenant/${t}/vat-settings`,
      usage: "VAT configuration · used by VAT return forms",
      pages: [{ label: "VAT Dashboard", route: "/vat-dashboard" }, { label: "VAT Periods", route: "/vat-periods" }] },
  ]},
  { group: "Companies House", endpoints: [
    { label: "CH — search (test)",
      path: "/companies-house/search?q=test",
      usage: "Proxies Companies House search · used by new client wizard and AD01",
      pages: [{ label: "Clients", route: "/client-profiles" }] },
  ]},
  { group: "Advisory", endpoints: [
    { label: "Advisory list",
      path: (t) => `/tenant/${t}/advisory`,
      usage: "Fetches advisory records for the tenant",
      pages: [{ label: "Advisory", route: "/advisory" }] },
  ]},
];

function Card({ children, sx = {} }) {
  return (
    <VuiBox p={2.5} sx={{ background: "#ffffff", border: "1px solid #d8e6f7", borderRadius: "12px", ...sx }}>
      {children}
    </VuiBox>
  );
}

function StatusChip({ result }) {
  if (!result) return (
    <Chip label="—" size="small"
      sx={{ height: 20, fontSize: FS.xs, color: TC.tertiary, bgcolor: "#f4f8ff", border: "1px solid #d8e6f7" }} />
  );
  if (result.loading) return <CircularProgress size={14} sx={{ color: TC.info }} />;
  const ok   = result.ok;
  const code = result.status ?? "ERR";
  const ms   = result.ms;
  return (
    <Tooltip title={result.error || `${ms}ms`} arrow>
      <Chip
        label={`${code} · ${ms}ms`} size="small"
        sx={{
          height: 20, fontSize: FS.xs, fontWeight: FW.bold, cursor: "default",
          bgcolor: ok ? "#e8f5e9" : code < 500 ? "#fff8e1" : "#fdecea",
          color:   ok ? "#2e7d32" : code < 500 ? "#e65100" : "#c62828",
          border:  `1px solid ${ok ? "#a5d6a7" : code < 500 ? "#ffcc80" : "#ef9a9a"}`,
        }}
      />
    </Tooltip>
  );
}

function HistoryRunRow({ run }) {
  const [open, setOpen] = useState(false);
  const entries         = Object.entries(run.results);
  const healthy         = entries.filter(([, r]) => r.ok).length;
  const total           = entries.length;
  const allOk           = healthy === total;
  const ts              = new Date(run.ranAt);
  const detailsId       = `healthcheck-run-${ts.getTime()}-details`;
  const failures        = entries.filter(([, r]) => !r.ok);

  return (
    <VuiBox mb={1.5} sx={{ border: "1px solid #d8e6f7", borderRadius: "10px", overflow: "hidden", background: "#ffffff" }}>
      <VuiBox component="button" type="button" display="flex" alignItems="center" justifyContent="space-between" px={2} py={1.2}
        aria-expanded={open}
        aria-controls={detailsId}
        onClick={() => setOpen((p) => !p)}
        sx={{ width: "100%", cursor: "pointer", border: "none", textAlign: "left", "&:hover": { background: "#f4f8ff" }, transition: "background 0.15s" }}>
        <VuiBox display="flex" alignItems="center" gap={2} flex={1} minWidth={0}>
          <VuiTypography variant="caption"
            sx={{ fontSize: FS.sm, color: TC.secondary, fontFamily: "monospace", flexShrink: 0 }}>
            {ts.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}{" "}
            {ts.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
          </VuiTypography>
          <Chip
            label={`${allOk ? "v " : ""}${healthy}/${total} OK`} size="small"
            sx={{
              height: 20, fontSize: FS.xs, fontWeight: FW.bold,
              bgcolor: allOk ? "#e8f5e9" : "#fdecea",
              color:   allOk ? "#2e7d32" : "#c62828",
              border:  `1px solid ${allOk ? "#a5d6a7" : "#ef9a9a"}`,
            }}
          />
          {failures.length > 0 && (
            <VuiTypography variant="caption" sx={{ fontSize: FS.xs, color: "#c62828" }} noWrap>
              Failed: {failures.map(([l]) => l).join(", ")}
            </VuiTypography>
          )}
        </VuiBox>
        <VuiTypography variant="caption" sx={{ fontSize: FS.sm, color: TC.tertiary, flexShrink: 0 }}>
          {open ? "^" : "v"}
        </VuiTypography>
      </VuiBox>

      <Collapse in={open}>
        <Divider sx={{ borderColor: "#d8e6f7" }} />
        <VuiBox id={detailsId} px={2} py={1.5} sx={{ background: "#f4f8ff" }}>
          {entries.map(([label, r]) => (
            <VuiBox key={label} display="flex" alignItems="center" justifyContent="space-between" py={0.5}>
              <VuiTypography variant="caption"
                sx={{ fontSize: FS.sm, color: r.ok ? TC.primary : "#c62828", fontWeight: FW.medium }}>
                {r.ok ? "+" : "x"} {label}
              </VuiTypography>
              <StatusChip result={r} />
            </VuiBox>
          ))}
        </VuiBox>
      </Collapse>
    </VuiBox>
  );
}

export default function HealthCheckPage() {
  const { tenantId }          = useAuthz();
  const history               = useHistory();
  const [results, setResults] = useState({});
  const [running, setRunning] = useState(false);
  const [lastRun, setLastRun] = useState(null);
  const [tab, setTab]         = useState("check");
  const [runs, setRuns]       = useState(() => loadHistory());

  useEffect(() => { setRuns(loadHistory()); }, []);

  const resolvePath = useCallback((ep) => {
    const raw = typeof ep.path === "function" ? ep.path(tenantId) : ep.path;
    return ep.query ? raw + ep.query(tenantId) : raw;
  }, [tenantId]);

  const runAll = useCallback(async () => {
    setRunning(true);
    const all = GROUPS.flatMap((g) => g.endpoints);
    const loading = {};
    all.forEach((ep) => { loading[ep.label] = { loading: true }; });
    setResults(loading);
    const snapshot = {};
    await Promise.all(all.map(async (ep) => {
      const res = await ping(resolvePath(ep), { auth: ep.auth !== false });
      snapshot[ep.label] = res;
      setResults((prev) => ({ ...prev, [ep.label]: res }));
    }));
    const now = new Date().toISOString();
    setLastRun(new Date(now));
    setRuns((prev) => saveHistory([{ ranAt: now, results: snapshot }, ...prev]));
    setRunning(false);
  }, [resolvePath]);

  const runOne = useCallback(async (ep) => {
    setResults((prev) => ({ ...prev, [ep.label]: { loading: true } }));
    const res = await ping(resolvePath(ep), { auth: ep.auth !== false });
    setResults((prev) => ({ ...prev, [ep.label]: res }));
  }, [resolvePath]);

  const clearHistory = () => { localStorage.removeItem(STORAGE_KEY); setRuns([]); };

  const allResults = Object.values(results).filter((r) => r && !r.loading);
  const healthy    = allResults.filter((r) => r.ok).length;
  const total      = GROUPS.flatMap((g) => g.endpoints).length;
  const ran        = allResults.length;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox px={3} pb={5}>

        <VuiBox display="flex" alignItems="center" justifyContent="space-between" mb={3} flexWrap="wrap" gap={2}>
          <VuiBox>
            <VuiTypography variant="h5" sx={{ color: TC.primary, fontWeight: FW.bold }}>
              API Health Check
            </VuiTypography>
            <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.sm }}>
              {API}{lastRun && ` - Last run: ${lastRun.toLocaleTimeString("en-GB")}`}
            </VuiTypography>
          </VuiBox>
          <VuiBox display="flex" alignItems="center" gap={2}>
            {ran > 0 && (
              <VuiTypography variant="caption" sx={{
                fontSize: FS.md, fontWeight: FW.semibold,
                color: healthy === ran ? "#2e7d32" : healthy < ran * 0.7 ? "#c62828" : "#e65100",
              }}>
                {healthy}/{ran} healthy
              </VuiTypography>
            )}
            <VuiButton variant="contained" color="info" size="small"
              disabled={running || !tenantId} onClick={runAll}
              sx={{ fontWeight: FW.bold, minWidth: 130 }}>
              {running
                ? <><CircularProgress size={12} sx={{ color: "#fff", mr: 1 }} />Running...</>
                : "Run All"}
            </VuiButton>
          </VuiBox>
        </VuiBox>

        <VuiBox display="flex" gap={1} mb={3}>
          {[["check", "Run Checks"], ["runs", `runs (${runs.length})`]].map(([key, label]) => (
            <VuiButton key={key} size="small"
              onClick={() => setTab(key)}
              sx={{
                fontSize: FS.sm, py: 0.5, px: 2,
                fontWeight: tab === key ? FW.bold : FW.normal,
                color:      tab === key ? TC.link : TC.secondary,
                background: tab === key ? "#e8f0fe" : "transparent",
                border:     `1px solid ${tab === key ? "#0057d8" : "#d8e6f7"}`,
                borderRadius: "8px",
                "&:hover": { background: "#f0f4ff" },
              }}>
              {label}
            </VuiButton>
          ))}
        </VuiBox>

        {!tenantId && (
          <VuiBox mb={2.5} p={1.5} sx={{ borderRadius: "10px", background: "#fff8e1", border: "1px solid #ffcc80" }}>
            <VuiTypography variant="caption" sx={{ color: "#e65100", fontSize: FS.sm }}>
              No tenant selected - tenant-scoped endpoints will fail.
            </VuiTypography>
          </VuiBox>
        )}

        {tab === "check" && (
          <>
            {GROUPS.map((group, gi) => (
              <Card key={gi} sx={{ mb: 2.5 }}>
                <VuiTypography variant="caption" sx={{
                  color: TC.secondary, fontSize: FS.xs, fontWeight: FW.bold,
                  textTransform: "uppercase", letterSpacing: "0.08em", display: "block", mb: 1.5,
                }}>
                  {group.group}
                </VuiTypography>
                <Divider sx={{ borderColor: "#d8e6f7", mb: 1.5 }} />
                {group.endpoints.map((ep, ei) => {
                  const result = results[ep.label];
                  const path   = resolvePath(ep);
                  return (
                    <VuiBox key={ei}
                      display="flex" alignItems="center" justifyContent="space-between"
                      py={0.9} px={1}
                      sx={{
                        borderRadius: "8px",
                        background: ei % 2 === 0 ? "transparent" : "#f4f8ff",
                        "&:hover": { background: "#e8f0fe" },
                        "&:hover .ep-pages": { opacity: 1 },
                        transition: "background 0.15s",
                      }}
                    >
                      <VuiBox display="flex" alignItems="center" gap={1.5} minWidth={0} flex={1}>
                        <Chip label="GET" size="small" sx={{
                          height: 20, fontSize: "0.6rem", fontWeight: FW.bold,
                          bgcolor: "#e3f2fd", color: "#1565c0", border: "1px solid #90caf9", flexShrink: 0,
                        }} />
                        <VuiBox minWidth={0}>
                          <VuiTypography variant="caption" sx={{
                            fontSize: FS.md, fontWeight: FW.semibold, color: TC.primary, display: "block",
                          }}>
                            {ep.label}
                          </VuiTypography>
                          {ep.usage && (
                            <VuiTypography variant="caption" sx={{
                              fontSize: FS.xs, color: TC.tertiary, display: "block", lineHeight: 1.3,
                            }}>
                              {ep.usage}
                            </VuiTypography>
                          )}
                          {/* Page links — visible on hover */}
                          {ep.pages && ep.pages.length > 0 && (
                            <VuiBox className="ep-pages" display="flex" gap={0.8} mt={0.5}
                              sx={{ opacity: 0, transition: "opacity 0.15s" }}>
                              {ep.pages.map((pg) => (
                                <Chip
                                  key={pg.route}
                                  label={
                                    <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
                                      {pg.label}
                                      <OpenInNewIcon sx={{ fontSize: 10 }} />
                                    </span>
                                  }
                                  size="small"
                                  onClick={() => history.push(pg.route)}
                                  sx={{
                                    height: 20, fontSize: FS.xs, fontWeight: FW.medium,
                                    bgcolor: "#e8f0fe", color: TC.link,
                                    border: "1px solid #90caf9",
                                    cursor: "pointer",
                                    "&:hover": { bgcolor: "#d0e4ff" },
                                  }}
                                />
                              ))}
                            </VuiBox>
                          )}
                        </VuiBox>
                        <VuiTypography variant="caption" sx={{
                          fontSize: FS.xs, color: TC.tertiary, fontFamily: "monospace",
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", ml: 1,
                        }}>
                          {path}
                        </VuiTypography>
                      </VuiBox>
                      <VuiBox display="flex" alignItems="center" gap={1} flexShrink={0} ml={1}>
                        <StatusChip result={result} />
                        <Tooltip title="Ping this endpoint" arrow>
                          <span>
                            <VuiButton size="small" variant="text" color="info"
                              disabled={result && result.loading} onClick={() => runOne(ep)}
                              sx={{ minWidth: 0, px: 0.8, py: 0.2, fontSize: FS.sm, color: TC.link }}>
                              Run
                            </VuiButton>
                          </span>
                        </Tooltip>
                      </VuiBox>
                    </VuiBox>
                  );
                })}
              </Card>
            ))}
            <VuiBox display="flex" gap={3} flexWrap="wrap" mt={1}>
              {[["2xx - OK", "#2e7d32"], ["4xx - Client error", "#e65100"], ["5xx / Network error", "#c62828"], ["Not run", TC.tertiary]].map(([l, c]) => (
                <VuiTypography key={l} variant="caption" sx={{ fontSize: FS.xs, color: c }}>{l}</VuiTypography>
              ))}
              <VuiTypography variant="caption" sx={{ fontSize: FS.xs, color: TC.tertiary, ml: "auto" }}>
                {total} endpoints total
              </VuiTypography>
            </VuiBox>
          </>
        )}

        {tab === "runs" && (
          <>
            <VuiBox display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <VuiTypography variant="caption" sx={{ fontSize: FS.sm, color: TC.secondary }}>
                Runs from the last 7 days -{" "}
                <strong style={{ color: TC.primary }}>{runs.length}</strong>{" "}
                run{runs.length !== 1 ? "s" : ""} stored
              </VuiTypography>
              {runs.length > 0 && (
                <VuiButton size="small" onClick={clearHistory}
                  sx={{ fontSize: FS.xs, color: TC.error, border: "1px solid #f4433644", borderRadius: "8px", px: 1.5, py: 0.3 }}>
                  Clear All
                </VuiButton>
              )}
            </VuiBox>
            {runs.length === 0 ? (
              <Card>
                <VuiTypography variant="caption" sx={{
                  color: TC.tertiary, fontSize: FS.sm, display: "block", textAlign: "center", py: 3,
                }}>
                  No runs yet. Run a health check to start recording.
                </VuiTypography>
              </Card>
            ) : (
              runs.map((run, i) => <HistoryRunRow key={i} run={run} />)
            )}
          </>
        )}

      </VuiBox>
    </DashboardLayout>
  );
}
